import { CheckboxInput, Colors } from "swing-components";

import { MarkdownContent } from "~components";
import type { CheckboxAnswerType } from "~onboarding/utils/types";
import { isArrayOfString } from "~utils";

type CheckboxAnswerProps = Omit<CheckboxAnswerType, "type"> & {
  /**
   * The value of the answer which will be de-serialized into an array of
   * values.
   */
  value?: string;
  /**
   * When an option is selected, pass the serialized array of values to the
   * parent.
   */
  onChange: (value?: string) => void;
  /**
   * Disables the editability of the answer.
   */
  isReadOnly?: boolean;
};

export function CheckboxAnswer(props: CheckboxAnswerProps) {
  const { options, value, onChange, isReadOnly } = props;
  const valuesSet = parseValueToSet(value);

  /*** Handlers ***/
  function handleOnChange(value: string) {
    if (valuesSet.has(value)) {
      valuesSet.delete(value);
    } else {
      valuesSet.add(value);
    }

    const values = Array.from(valuesSet);

    // When there are no values selected, send `undefined` to the parent.
    if (values.length === 0) return onChange(undefined);
    // When there are values, stringify them and send them to the parent.
    onChange(JSON.stringify(values));
  }

  /*** Render ***/
  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {options.map(({ label, value, description, hasContainer }, index) => {
        const isLast = index === options.length - 1;

        return (
          <>
            <CheckboxInput
              isDisabled={isReadOnly}
              label={<MarkdownContent body={label} />}
              isChecked={valuesSet.has(value)}
              onChange={() => handleOnChange(value)}
              description={description}
              hasContainer={hasContainer}
            />

            {/*
              Divider
              Only render when there is no container around the checkbox option.
            */}
            {!hasContainer && !isLast && (
              <hr
                css={{
                  height: 1,
                  backgroundColor: Colors.slate200,
                  border: "none",
                  // NOTE: Using negative margins to offset the padding between
                  // items due to having this divider.
                  marginTop: -8,
                  marginBottom: -8,
                  marginLeft: 0,
                  marginRight: 0,
                }}
              />
            )}
          </>
        );
      })}
    </div>
  );
}

/***** Helper Functions *****/
/**
 * Convert a serialized JSON array of strings into a Set<string>.
 *
 * - When `value` is undefined, returns an empty Set.
 * - On invalid JSON or invalid element types, returns an empty Set.
 */
function parseValueToSet(value: string | undefined) {
  if (value) {
    try {
      const values = JSON.parse(value);
      isArrayOfString(values);
      return new Set(values);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      return new Set();
    }
  }
  return new Set();
}
